import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../api/api";
import { jwtDecode } from "jwt-decode";



/* -----------------------------
   Helper: Decode token
--------------------------------*/
const decodeToken = (token) => {
  if (token) {
    try {
      return jwtDecode(token);
    } catch {
      return "";
    }
  }
  return "";
};

/* -----------------------------
   Google Login
--------------------------------*/
export const google_login = createAsyncThunk(
  "auth/google_login",
  async (googleUser, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post("/customer/google-login", googleUser);
      if (data.token) localStorage.setItem("customerToken", data.token);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Google login failed" });
    }
  }
);

/* -----------------------------
   Customer Auth Register
--------------------------------*/


export const customer_auth = createAsyncThunk(
  "auth/customer_auth",
  async (info, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post("/customer/customer-auth", info);

      if (data.token) {
        localStorage.setItem("customerToken", data.token);
      }

      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Auth failed" });
    }
  }
);


/* -----------------------------
   Normal Register
--------------------------------*/
export const customer_register = createAsyncThunk(
  "auth/customer_register",
  async (info, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post("/customer/customer-register", info);
      localStorage.setItem("customerToken", data.token);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

/* -----------------------------
   Normal Login
--------------------------------*/
export const customer_login = createAsyncThunk(
  "auth/customer_login",
  async (info, { rejectWithValue, fulfillWithValue }) => {
    try {
      const { data } = await api.post("/customer/customer-login", info);
      localStorage.setItem("customerToken", data.token);
      return fulfillWithValue(data);
    } catch (error) {
      return rejectWithValue(error.response?.data);
    }
  }
);

/* -----------------------------
   Send OTP for Login
--------------------------------*/
export const sendOtpLogin = createAsyncThunk(
  "auth/sendOtpLogin",
  async (userData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/customer/send-otp-login", userData, {
        withCredentials: true,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to send OTP" });
    }
  }
);

/* -----------------------------
   Send OTP for Registration
--------------------------------*/
export const sendOtpRegister = createAsyncThunk(
  "auth/sendOtpRegister",
  async (userData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/customer/send-otp-register", userData, {
        withCredentials: true,
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to send OTP" });
    }
  }
);

/* -----------------------------
   Verify OTP + Registration
--------------------------------*/
export const verifyOtpAndRegister = createAsyncThunk(
  "auth/verifyOtpAndRegister",
  async (otpData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/customer/verify-otp-register", otpData, {
        withCredentials: true,
      });
      if (data.token) localStorage.setItem("customerToken", data.token);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "OTP verification failed" });
    }
  }
);

/* -----------------------------
   Verify OTP + Login
--------------------------------*/
export const verifyOtpLogin = createAsyncThunk(
  "auth/verifyOtpLogin",
  async (otpData, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/customer/verify-otp-login", otpData, {
        withCredentials: true,
      });
      if (data.token) localStorage.setItem("customerToken", data.token);
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "OTP login failed" });
    }
  }
);

/* -----------------------------
   Save Address
--------------------------------*/
export const saveAddress = createAsyncThunk(
  "auth/saveAddress",
  async (address, { rejectWithValue }) => {
    try {
      const { data } = await api.post("/customer/save-address", address, {
  withCredentials: true, // ✅ needed to send cookies
});

      return data.addresses; // returns updated address array
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to save address" });
    }
  }
);


/* -----------------------------
   Get Saved Addresses
--------------------------------*/
export const getAddresses = createAsyncThunk(
  "auth/getAddresses",
  async (_, { rejectWithValue }) => {
    try {
      const { data } = await api.get("/customer/get-addresses", {
  withCredentials: true, // ✅ needed to send cookies
});
      return data.addresses;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: "Failed to fetch addresses" });
    }
  }
);

/* -----------------------------
   Reducer Slice
--------------------------------*/
export const authReducer = createSlice({
  name: "auth",
  initialState: {
    loader: false,
    userInfo: decodeToken(localStorage.getItem("customerToken")),
    errorMessage: "",
    successMessage: "",
    addresses: [], // store saved addresses
  },
  reducers: {
    messageClear: (state) => {
      state.errorMessage = "";
      state.successMessage = "";
    },
    user_reset: (state) => {
      state.userInfo = "";
      state.addresses = [];
      localStorage.removeItem("customerToken");
    },
  },
  extraReducers: (builder) => {
    builder
      // Manual Register
      .addCase(customer_register.pending, (state) => { state.loader = true; })
      .addCase(customer_register.rejected, (state, { payload }) => { state.errorMessage = payload.error; state.loader = false; })
      .addCase(customer_register.fulfilled, (state, { payload }) => { state.successMessage = payload.message; state.loader = false; state.userInfo = decodeToken(payload.token); })

      // Manual Login
      .addCase(customer_login.pending, (state) => { state.loader = true; })
      .addCase(customer_login.rejected, (state, { payload }) => { state.errorMessage = payload.error; state.loader = false; })
      .addCase(customer_login.fulfilled, (state, { payload }) => { state.successMessage = payload.message; state.loader = false; state.userInfo = decodeToken(payload.token); })

      // Google Login
      .addCase(google_login.pending, (state) => { state.loader = true; })
      .addCase(google_login.rejected, (state, { payload }) => { state.errorMessage = payload.error; state.loader = false; })
      .addCase(google_login.fulfilled, (state, { payload }) => { state.successMessage = payload.message || "Google login successful"; state.loader = false; state.userInfo = decodeToken(payload.token); })

      // Send OTP Login
      .addCase(sendOtpLogin.pending, (state) => { state.loader = true; })
      .addCase(sendOtpLogin.rejected, (state, { payload }) => { state.errorMessage = payload.error; state.loader = false; })
      .addCase(sendOtpLogin.fulfilled, (state, { payload }) => { state.successMessage = payload.message; state.loader = false; })

      // Send OTP Register
      .addCase(sendOtpRegister.pending, (state) => { state.loader = true; })
      .addCase(sendOtpRegister.rejected, (state, { payload }) => { state.errorMessage = payload.error; state.loader = false; })
      .addCase(sendOtpRegister.fulfilled, (state, { payload }) => { state.successMessage = payload.message; state.loader = false; })

      // Verify OTP Registration
      .addCase(verifyOtpAndRegister.pending, (state) => { state.loader = true; })
      .addCase(verifyOtpAndRegister.rejected, (state, { payload }) => { state.errorMessage = payload.error; state.loader = false; })
      .addCase(verifyOtpAndRegister.fulfilled, (state, { payload }) => { state.successMessage = payload.message; state.loader = false; state.userInfo = payload.token ? decodeToken(payload.token) : null; })

      // Verify OTP Login
      .addCase(verifyOtpLogin.pending, (state) => { state.loader = true; })
      .addCase(verifyOtpLogin.rejected, (state, { payload }) => { state.errorMessage = payload.error; state.loader = false; })
      .addCase(verifyOtpLogin.fulfilled, (state, { payload }) => { state.successMessage = payload.message; state.loader = false; state.userInfo = payload.token ? decodeToken(payload.token) : null; })

      // Save Address
      .addCase(saveAddress.pending, (state) => { state.loader = true; })
      .addCase(saveAddress.rejected, (state, { payload }) => { state.errorMessage = payload.error; state.loader = false; })
      .addCase(saveAddress.fulfilled, (state, { payload }) => { state.addresses = payload; state.loader = false; })

      // Get Addresses
      .addCase(getAddresses.pending, (state) => { state.loader = true; })
      .addCase(getAddresses.rejected, (state, { payload }) => { state.errorMessage = payload.error; state.loader = false; })
      .addCase(getAddresses.fulfilled, (state, { payload }) => { state.addresses = payload; state.loader = false; })


      .addCase(customer_auth.pending, (state) => {
        state.loader = true;
        state.errorMessage = "";
      })
      .addCase(customer_auth.fulfilled, (state, action) => {
        state.loader = false;
        state.userInfo = action.payload.user;
        state.successMessage = action.payload.message;
      })
      .addCase(customer_auth.rejected, (state, action) => {
        state.loader = false;
        state.errorMessage = action.payload?.error || "Something went wrong";
      })
  },
});

export const { messageClear, user_reset } = authReducer.actions;
export default authReducer.reducer;
